import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { HelpCircle, ArrowRight, Shield, Code, Globe, DollarSign } from 'lucide-react';

const TechnicalFAQ = () => {
  const faqSections = [
    {
      title: 'Integration & Setup',
      icon: Code,
      items: [
        {
          id: 'custom-objects',
          question: 'How does NoQ5.ai handle mapping of custom Salesforce and HubSpot objects?',
          answer: (
            <>
              <p className="mb-4">
                NoQ5.ai automatically discovers your CRM schema using the Describe API (Salesforce) and Properties API (HubSpot). When you speak, our AI identifies which object and fields you're referencing, validates field types, and writes the data correctly.
              </p>
              <p className="mb-4"><strong>This works for ALL custom objects and fields without any configuration needed.</strong></p>
              <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                <p className="text-sm font-semibold mb-2 text-foreground">Example:</p>
                <p className="text-sm text-muted-foreground">Voice: "Update custom project Alpha to status In Progress"</p>
                <p className="text-sm text-muted-foreground mt-2">→ NoQ5.ai discovers Custom_Project__c object</p>
                <p className="text-sm text-muted-foreground">→ Maps "In Progress" to Status__c picklist field</p>
                <p className="text-sm text-muted-foreground">→ Executes PATCH request with correct field API names</p>
              </div>
            </>
          ),
        },
        {
          id: 'oauth-control',
          question: 'How does OAuth2 token management work in a multi-tenant environment?',
          answer: (
            <>
              <p className="mb-4">Each organization's OAuth tokens are:</p>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li><strong className="text-foreground">Encrypted at rest</strong> using AES-256-GCM encryption</li>
                <li><strong className="text-foreground">Stored in isolated database partitions</strong> with tenant_id segregation</li>
                <li><strong className="text-foreground">Automatically refreshed</strong> before expiration with zero downtime</li>
                <li><strong className="text-foreground">Never accessible across tenants</strong> due to database-level row security policies</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
                <p className="text-sm text-foreground">
                  <strong>Security Note:</strong> Even if a database compromise occurred, tokens are encrypted with organization-specific keys stored in a separate key management service (AWS KMS).
                </p>
              </div>
            </>
          ),
        },
        {
          id: 'workflow-integration',
          question: 'Can NoQ5.ai integrate with our existing Salesforce/HubSpot workflows and automation?',
          answer: (
            <>
              <p className="mb-4">
                <strong>Yes, NoQ5.ai works alongside all your existing automation without conflicts.</strong>
              </p>
              <p className="mb-4 text-muted-foreground">When NoQ5.ai updates a CRM record:</p>
              <ul className="space-y-2 list-disc list-inside mb-4 text-muted-foreground">
                <li>Salesforce Process Builder, Flows, and Apex triggers execute normally</li>
                <li>HubSpot workflows and automations fire as expected</li>
                <li>Field history tracking records the change with NoQ5.ai as the source</li>
                <li>Validation rules and required fields are respected</li>
              </ul>
              <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                <p className="text-sm text-foreground">
                  <strong>Use Case:</strong> If you have a Salesforce Flow that sends an email when an Opportunity stage changes to "Closed Won", that Flow will trigger when NoQ5.ai updates the stage via voice command.
                </p>
              </div>
            </>
          ),
        },
        {
          id: 'setup-time',
          question: 'How long does setup take?',
          answer: (
            <>
              <p className="mb-4">
                <strong>Setup takes less than 5 minutes:</strong>
              </p>
              <ol className="space-y-2 list-decimal list-inside text-muted-foreground">
                <li>Click "Connect Salesforce" or "Connect HubSpot"</li>
                <li>Authorize NoQ5.ai via OAuth (standard CRM login)</li>
                <li>Start speaking to update your CRM immediately</li>
              </ol>
              <p className="mt-4 text-foreground">
                <strong>No training, no field mapping, no admin configuration needed.</strong> NoQ5.ai automatically discovers your schema and learns your terminology.
              </p>
            </>
          ),
        },
      ],
    },
    {
      title: 'Security & Compliance',
      icon: Shield,
      items: [
        {
          id: 'gdpr',
          question: 'Is NoQ5.ai GDPR compliant for European companies?',
          answer: (
            <>
              <p className="mb-4"><strong>Yes. NoQ5.ai is fully GDPR compliant with:</strong></p>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li><strong className="text-foreground">EU data residency:</strong> All data stored in Ireland (AWS EU-West-1)</li>
                <li><strong className="text-foreground">Built-in data anonymization</strong> for sensitive fields (PII, phone numbers, etc.)</li>
                <li><strong className="text-foreground">Right to deletion:</strong> One-click data export and account deletion</li>
                <li><strong className="text-foreground">Data portability:</strong> Export API available</li>
                <li><strong className="text-foreground">DPA (Data Processing Agreement)</strong> available for enterprise customers</li>
                <li><strong className="text-foreground">No data transfer outside EU</strong> - voice processing uses EU endpoints</li>
                <li><strong className="text-foreground">Regular security audits</strong> and ISO 27001 certification (in progress)</li>
              </ul>
            </>
          ),
        },
        {
          id: 'data-isolation',
          question: 'How is our data isolated from other organizations?',
          answer: (
            <>
              <p className="mb-4 text-muted-foreground">NoQ5.ai uses a multi-layered isolation approach:</p>
              <ul className="space-y-3">
                <li>
                  <strong className="text-foreground">Database Level:</strong>
                  <p className="text-sm text-muted-foreground">Row-level security policies ensure queries can only access data matching your tenant_id. Attempts to access other organizations' data fail at the database layer.</p>
                </li>
                <li>
                  <strong className="text-foreground">Application Level:</strong>
                  <p className="text-sm text-muted-foreground">All API requests require authentication and are scoped to the authenticated organization. No cross-tenant operations are possible.</p>
                </li>
                <li>
                  <strong className="text-foreground">Token Level:</strong>
                  <p className="text-sm text-muted-foreground">OAuth tokens are encrypted per-organization and cannot be used to access another organization's CRM.</p>
                </li>
              </ul>
            </>
          ),
        },
        {
          id: 'certifications',
          question: 'What security certifications does NoQ5.ai have?',
          answer: (
            <>
              <p className="mb-4"><strong>Current and In Progress:</strong></p>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li><strong className="text-foreground">Official Salesforce Partner</strong> (verified)</li>
                <li><strong className="text-foreground">ISO 27001</strong> (certification in progress)</li>
                <li><strong className="text-foreground">GDPR Compliance</strong> (certified by EU legal counsel)</li>
              </ul>
            </>
          ),
        },
      ],
    },
    {
      title: 'Languages & Localization',
      icon: Globe,
      items: [
        {
          id: 'languages',
          question: 'What languages does NoQ5.ai support?',
          answer: (
            <>
              <p className="mb-4"><strong>Currently supported:</strong></p>
              <ul className="space-y-1 list-disc list-inside mb-4 text-muted-foreground">
                <li>English (US, UK, AU)</li>
                <li>French</li>
                <li>Spanish</li>
                <li>German</li>
                <li>Italian</li>
              </ul>
              <p className="text-foreground"><strong>Coming soon:</strong> Dutch, Portuguese, Swedish, Danish, Norwegian</p>
              <div className="bg-primary/5 p-4 rounded-lg mt-4 border border-primary/10">
                <p className="text-sm text-muted-foreground">
                  NoQ5.ai automatically detects your language and maps voice input to CRM field values in the correct language, respecting your CRM's language settings.
                </p>
              </div>
            </>
          ),
        },
        {
          id: 'regional',
          question: 'Does NoQ5.ai handle regional date/number formats?',
          answer: (
            <>
              <p className="mb-4">
                <strong>Yes.</strong> NoQ5.ai respects your CRM's locale settings and automatically formats:
              </p>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li><strong className="text-foreground">Dates:</strong> DD/MM/YYYY (EU) vs MM/DD/YYYY (US)</li>
                <li><strong className="text-foreground">Numbers:</strong> 1.000,50 (EU) vs 1,000.50 (US)</li>
                <li><strong className="text-foreground">Currency:</strong> €1.000 vs $1,000</li>
                <li><strong className="text-foreground">Phone numbers:</strong> +33, +44, +1, etc.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    {
      title: 'Pricing & ROI',
      icon: DollarSign,
      items: [
        {
          id: 'pricing',
          question: 'What does NoQ5.ai cost?',
          answer: (
            <>
              <p className="mb-4"><strong>Simple, transparent pricing with a 7-day free trial:</strong></p>
              <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                <li><strong className="text-foreground">€59/user/month</strong> (annual billing, 12-month commitment)</li>
                <li><strong className="text-foreground">Volume discounts</strong> available for teams of 5+ users</li>
              </ul>
              <p className="mt-4 text-muted-foreground">Includes voice & chat interface, automatic CRM updates, and native Salesforce & HubSpot integration.</p>
              <div className="bg-green-500/10 border border-green-500/20 p-4 rounded-lg mt-4">
                <p className="text-sm text-foreground">
                  <strong>Start Free:</strong> Try NoQ5.ai free for 7 days. No credit card required.
                </p>
              </div>
            </>
          ),
        },
        {
          id: 'roi',
          question: "What's the ROI? How much time does NoQ5.ai actually save?",
          answer: (
            <>
              <p className="mb-4"><strong>Average time savings based on beta user data:</strong></p>
              <div className="bg-primary/5 p-6 rounded-lg mb-4 border border-primary/10">
                <ul className="space-y-3">
                  <li className="flex justify-between">
                    <span className="text-foreground"><strong>Manual CRM update:</strong></span>
                    <span className="text-destructive font-medium">5-10 minutes</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-foreground"><strong>NoQ5.ai voice update:</strong></span>
                    <span className="text-primary font-medium">30 seconds</span>
                  </li>
                  <li className="flex justify-between border-t border-border pt-3 mt-3">
                    <span className="text-foreground"><strong>Time saved per update:</strong></span>
                    <span className="text-primary font-bold">8+ minutes</span>
                  </li>
                </ul>
              </div>
              <p className="text-muted-foreground">
                For a rep making 10 CRM updates per day, that's <strong className="text-foreground">80+ minutes saved daily</strong> or <strong className="text-foreground">8+ hours per week</strong> returned to selling.
              </p>
            </>
          ),
        },
      ],
    },
  ];

  return (
    <Layout>
      <SEOHead 
        title="NoQ5.ai FAQ: Salesforce & HubSpot AI Copilot Integration Questions"
        description="Technical FAQ about NoQ5.ai: OAuth2 setup, custom object mapping, GDPR compliance, pricing (€59/mo), and ROI. Voice AI for Salesforce & HubSpot."
        canonicalUrl="https://noq5.ai/faq"
        keywords="NoQ5.ai FAQ, Salesforce AI copilot, HubSpot AI copilot, Salesforce Einstein alternative, CRM voice assistant, GDPR compliant AI, sales AI tool"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqSections.flatMap(section =>
            section.items.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": typeof item.answer === 'string' ? item.answer : item.question
              }
            }))
          )
        }]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-12"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4"
            >
              <HelpCircle className="w-8 h-8 text-primary" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Technical FAQ
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Common technical questions about NoQ5.ai integration with Salesforce and HubSpot
            </p>
          </div>

          {/* FAQ Sections */}
          {faqSections.map((section, sectionIndex) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * sectionIndex }}
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-foreground">
                <section.icon className="w-6 h-6 text-primary" />
                {section.title}
              </h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {section.items.map((item) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    className="bg-card rounded-xl border border-border px-6 data-[state=open]:border-primary/30 transition-colors"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="font-semibold text-foreground pr-4">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pt-2 pb-6">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.section>
          ))}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">Still have questions?</h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Our team is happy to help. Start a free trial or reach out directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
                asChild
              >
                <a href="https://app.noq5.ai/trial" target="_blank" rel="noopener noreferrer">
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold px-8 py-6 text-lg rounded-xl"
                asChild
              >
                <a href="mailto:hello@noq5.ai">
                  Contact Us
                </a>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default TechnicalFAQ;